import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { MetaTags } from '../../../../components/SEO/MetaTags';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
export function BlogPostNarcissismGaslightingDE() {
  const articleData = {
    title: 'Erfahren Sie mehr über den Begriff: Narzissmus & Gaslighting',
    description: 'Verwirrt durch narzisstisches oder Gaslighting-Verhalten? Lernen Sie die wichtigsten Anzeichen in Texten kennen und sehen Sie, wie die KI von Toxic Detector subtile Manipulationsmuster sofort erkennt.',
    slug: 'narcissism-gaslighting-explained',
    publishDate: '2025-07-15',
    author: 'Toxic Detector Redaktion'
  };
  const alternateUrls = [{
    url: 'https://toxicdetector.com/de/blog/narcissism-gaslighting-explained',
    language: 'de'
  }, {
    url: 'https://toxicdetector.com/en/blog/narcissism-gaslighting-explained',
    language: 'en'
  }];
  const faqSchema = [{
    '@type': 'Question',
    name: 'Ist es normal, sich durch narzisstisches oder Gaslighting-Verhalten verwirrt oder ausgelaugt zu fühlen?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Ja. Diese Verhaltensweisen sind darauf ausgelegt, die Wahrnehmung zu verzerren und emotionale Energie zu entziehen, weshalb es wichtig ist, Muster frühzeitig zu erkennen.'
    }
  }];
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.title,
    description: articleData.description,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/s4H6uMqaiw9nBMD5NJ8Nx3/1763122555521.jpg',
    datePublished: articleData.publishDate,
    author: {
      '@type': 'Organization',
      name: articleData.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector',
      logo: {
        '@type': 'ImageObject',
        url: 'https://toxicdetector.com/images/logo.png'
      }
    }
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: 'https://toxicdetector.com/'
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('Link in die Zwischenablage kopiert!');
    }
  };
  return <>
      <MetaTags title="Narzissmus & Gaslighting erklärt — Wie KI Manipulation erkennt | ToxicDetector" description="Verwirrt durch narzisstisches oder Gaslighting-Verhalten? Lernen Sie die wichtigsten Anzeichen in Texten kennen und sehen Sie, wie die KI von Toxic Detector subtile Manipulationsmuster sofort erkennt." keywords="Narzissmus und Gaslighting, verdeckter Narzisst Anzeichen, offenes narzisstisches Verhalten, Textmanipulationsmuster, emotionale Manipulation in Beziehungen, KI Gaslighting Detektor" canonical={`https://toxicdetector.com/de/blog/${articleData.slug}`} language="de" alternateUrls={alternateUrls} ogType="article" ogImage="https://cdn.magicpatterns.com/uploads/iLMUMmEFFWiRSqv8DSTAdz/file_00000000f0c8720a9c4e7f9e2fbdea6d.png" schema={[faqSchema, articleSchema]} data-id="element-4619" />

      <div className="flex flex-col w-full min-h-screen bg-white" data-id="element-4620">
        <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50" data-id="element-4621">
          <div className="max-w-4xl mx-auto px-6 py-5" data-id="element-4622">
            <div className="flex items-center justify-between" data-id="element-4623">
              <Link to="/" className="flex items-center gap-2 group" data-id="element-4624">
                <span className="text-2xl" data-id="element-4625">🚩</span>
                <span className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition-colors" data-id="element-4626">
                  ToxicDetector
                </span>
              </Link>
              <button onClick={handleShare} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-4627">
                <Share2 size={20} className="text-gray-600" data-id="element-4628" />
              </button>
            </div>
          </div>
        </header>

        <article className="max-w-4xl mx-auto w-full px-6 py-16" data-id="element-4629">
          <Link to="/" className="inline-flex items-center text-gray-600 text-sm mb-12 hover:text-gray-900 transition-colors group" data-id="element-4630">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-4631" />
            Zurück zur Startseite
          </Link>

          <header className="mb-16" data-id="element-4632">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight leading-[1.1] text-gray-900" data-id="element-4633">
              {articleData.title}
            </h1>
            <div className="flex items-center text-gray-500 text-sm gap-3" data-id="element-4634">
              <time dateTime={articleData.publishDate} data-id="element-4635">
                {new Date(articleData.publishDate).toLocaleDateString('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </time>
              <span className="text-gray-300" data-id="element-4636">•</span>
              <span data-id="element-4637">{articleData.author}</span>
            </div>
          </header>

          <div className="prose prose-lg max-w-none" data-id="element-4638">
            <p className="text-2xl mb-10 leading-relaxed text-gray-700 font-medium" data-id="element-4639">
              Narzissmus und Gaslighting sind Begriffe, die oft verwendet
              werden, aber was bedeuten sie wirklich? Und wie hängen sie
              zusammen?
            </p>

            <section className="mb-16" data-id="element-4640">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-4641">
                Was ist Narzissmus?
              </h2>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-4642">
                Narzissmus ist mehr als nur Eitelkeit. Es ist ein
                Persönlichkeitsmerkmal, das durch ein übersteigertes
                Selbstwertgefühl, ein tiefes Bedürfnis nach Bewunderung und
                einen Mangel an Empathie gekennzeichnet ist.
              </p>
            </section>

            <section className="mb-16" data-id="element-4643">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-4644">
                Was ist Gaslighting?
              </h2>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-4645">
                Gaslighting ist eine Form der psychologischen Manipulation, bei
                der eine Person Zweifel an der Wahrnehmung, dem Gedächtnis oder
                dem Urteilsvermögen einer anderen Person sät.
              </p>
            </section>

            <section className="mb-16" data-id="element-4646">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-4647">
                Die Verbindung
              </h2>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-4648">
                Narzissten nutzen Gaslighting oft als Werkzeug, um Kontrolle zu
                behalten und ihr Selbstbild zu schützen. Es ist eine gefährliche
                Kombination.
              </p>
            </section>
          </div>

          <div className="bg-purple-600 rounded-2xl p-12 md:p-16 text-center text-white shadow-xl mt-16" data-id="element-4649">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight" data-id="element-4650">
              Erkennen Sie die Zeichen.
            </h2>
            <p className="text-purple-100 mb-10 text-xl leading-relaxed max-w-2xl mx-auto" data-id="element-4651">
              Lassen Sie sich nicht manipulieren. Nutzen Sie KI, um Klarheit zu
              gewinnen.
            </p>
            <Link to="/upload" className="inline-block bg-white text-purple-600 px-12 py-5 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl hover:scale-105" data-id="element-4652">
              Jetzt scannen →
            </Link>
          </div>

          <div className="mt-20" data-id="element-4653">
            <RelatedBlogPosts posts={[{
            title: '7 Anzeichen von Gaslighting',
            description: 'Entdecken Sie 7 Anzeichen von Gaslighting, die unsere KI sofort erkennt.',
            slug: '7-signs-of-gaslighting',
            emoji: '💀'
          }, {
            title: 'Ist dein Ex ein verdeckter Narzisst?',
            description: 'Entdecke, ob dein Ex Anzeichen von verdecktem narzisstischem Verhalten zeigt.',
            slug: 'is-your-ex-a-covert-narcissist',
            emoji: '🎭'
          }]} data-id="element-4654" />
          </div>
        </article>

        <footer className="bg-gray-900 text-white py-16 mt-24" data-id="element-4655">
          <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-4656">
            <p className="text-gray-400 text-sm" data-id="element-4657">
              © {new Date().getFullYear()} ToxicDetector. Alle Rechte
              vorbehalten.
            </p>
          </div>
        </footer>
      </div>
    </>;
}